import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  CheckCircle,
  Shield,
  Star,
  ArrowRight,
  Calendar,
  Wrench,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Camera,
  Zap,
  Droplets,
  Scissors
} from 'lucide-react';

const SewerCleaningPricing = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Real Sewer Cleaning Projects
  const sewerCleaningProjects = [
    {
      id: 1,
      image: '/colorado-plumbing-uploads/existing-projects/sewer-repair-experts.png',
      location: 'Denver Metro, CO',
      type: 'Sewer Cleaning Specialists',
      description: 'Professional sewer cleaning team with Milwaukee descaling machines and sewer cameras',
      features: ['Milwaukee Descale Machine', 'Sewer Camera Verification', 'Root Removal Specialists']
    },
    {
      id: 2,
      image: '/colorado-plumbing-uploads/existing-projects/best-sewer-contractor.png',
      location: 'Colorado Springs, CO',
      type: 'Advanced Sewer Services',
      description: 'Complete sewer cleaning from basic snaking to advanced hydro-jetting and descaling',
      features: ['Hydro-Jetting', 'Professional Descaling', 'Emergency Service']
    },
    {
      id: 3,
      image: '/colorado-plumbing-uploads/existing-projects/sewer-dig-double-cleanout.png',
      location: 'Highlands Ranch, CO',
      type: 'Sewer Line Access & Cleaning',
      description: 'Professional sewer line cleaning with proper cleanout access for thorough maintenance',
      features: ['Double Cleanout Access', 'Complete Line Cleaning', 'Professional Installation']
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % sewerCleaningProjects.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [sewerCleaningProjects.length]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % sewerCleaningProjects.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + sewerCleaningProjects.length) % sewerCleaningProjects.length);
  };

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <div className="min-h-screen bg-neutral-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy">
        <div className="container mx-auto px-4 text-center relative z-10 py-16 md:py-24">
          <div className="inline-block px-6 py-3 mb-8 rounded-full bg-action-urgent/90 text-white font-bold text-lg">
            🔧 Professional Sewer Cleaning • Milwaukee Equipment • Camera Verification
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white leading-tight">
            Sewer Cleaning Services
            <span className="block text-2xl md:text-3xl text-trust-light font-semibold mt-2">
              Complete Drain & Sewer Cleaning Solutions
            </span>
          </h1>
          
          <p className="text-lg text-trust-light/90 mb-8">
            Master Plumber Owned • 7+ Years Denver Metro • 293+ Reviews • Specialized Equipment & Expertise
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            
            <a 
              href="#sewer-cleaning-services" 
              className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
            >
              🔧 View All Services
            </a>
          </div>
        </div>
      </section>

      {/* Specialized Sewer Cleaning Services Section */}
      <section id="sewer-cleaning-services" className="py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Specialized Sewer Cleaning Services
            </h2>
            <p className="text-xl text-blue-100 mb-4">
              Choose the right sewer cleaning service for your specific needs
            </p>
            <div className="inline-flex items-center bg-success-light px-6 py-3 rounded-full border-2 border-success-primary/20">
              <Shield className="w-6 h-6 text-success-primary mr-3" />
              <span className="text-success-primary font-bold">5 Specialized Services • Professional Equipment • Transparent Pricing</span>
            </div>
          </div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            
            {/* Snake/Auger Service Card */}
            <div className="group bg-gradient-to-br from-trust-primary to-trust-deep rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:scale-[1.02] border border-trust-primary/20">
              <div className="p-8 text-white relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform relative z-10">
                  <Wrench size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Snake/Auger Services</h3>
                <p className="text-trust-light mb-6 leading-relaxed relative z-10">
                  Traditional drain and sewer snaking with professional equipment. Effective for common clogs and blockages.
                </p>
                <div className="space-y-3 mb-6 relative z-10">
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">Toilet auger service: $225</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">Mainline snaking: $395-$695</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">Branch lines: $225-$325</span>
                  </div>
                </div>
                <a 
                  href="/sewer-snake-auger-pricing"
                  className="inline-flex items-center bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-full font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 w-full justify-center border border-white/20 relative z-10"
                >
                  View Snake/Auger Pricing
                  <ArrowRight size={16} className="ml-2" />
                </a>
              </div>
            </div>

            {/* Hydro-Jetting Service Card */}
            <div className="group bg-gradient-to-br from-action-primary to-action-urgent rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:scale-[1.02] border border-action-primary/20">
              <div className="p-8 text-white relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform relative z-10">
                  <Droplets size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Hydro-Jetting</h3>
                <p className="text-action-warm/80 mb-6 leading-relaxed relative z-10">
                  High-pressure water jetting for thorough cleaning. Removes grease, scale, and minor roots effectively.
                </p>
                <div className="space-y-3 mb-6 relative z-10">
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">Mainline jetting: $695</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">Kitchen drains (Milwaukee Descale Grease Chains and Flush. Better than jetting): $495</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">Branch lines: $350-$450</span>
                  </div>
                </div>
                <a 
                  href="/hydro-jetting-pricing"
                  className="inline-flex items-center bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-full font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 w-full justify-center border border-white/20 relative z-10"
                >
                  View Jetting Pricing
                  <ArrowRight size={16} className="ml-2" />
                </a>
              </div>
            </div>

            {/* Cast Iron Descaling Service Card */}
            <div className="group bg-gradient-to-br from-success-primary to-success-eco rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:scale-[1.02] border border-success-primary/20">
              <div className="p-8 text-white relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform relative z-10">
                  <Camera size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Cast Iron Descaling</h3>
                <p className="text-success-light/90 mb-6 leading-relaxed relative z-10">
                  Milwaukee descaling machines remove heavy scale and tuberculation from cast iron pipes.
                </p>
                <div className="space-y-3 mb-6 relative z-10">
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-yellow-300" />
                    <span className="text-sm font-medium">Mainline descaling: $1,250-$1,350</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-yellow-300" />
                    <span className="text-sm font-medium">Kitchen lines: $575</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-yellow-300" />
                    <span className="text-sm font-medium">Branch lines: $425-$650</span>
                  </div>
                </div>
                <a 
                  href="/cast-iron-descaling-pricing"
                  className="inline-flex items-center bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-full font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 w-full justify-center border border-white/20 relative z-10"
                >
                  View Descaling Pricing
                  <ArrowRight size={16} className="ml-2" />
                </a>
              </div>
            </div>

            {/* Root Removal Service Card */}
            <div className="group bg-gradient-to-br from-action-urgent to-red-700 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:scale-[1.02] border border-action-urgent/20">
              <div className="p-8 text-white relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform relative z-10">
                  <Scissors size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Root Removal</h3>
                <p className="text-red-100 mb-6 leading-relaxed relative z-10">
                  Specialized root cutting equipment with camera documentation of root intrusion.
                </p>
                <div className="space-y-3 mb-6 relative z-10">
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-yellow-300" />
                    <span className="text-sm font-medium">Root clearing with pictures: $695</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-yellow-300" />
                    <span className="text-sm font-medium">With video recording: $795</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-yellow-300" />
                    <span className="text-sm font-medium">Via pulled toilet: $995</span>
                  </div>
                </div>
                <a 
                  href="/root-removal-pricing"
                  className="inline-flex items-center bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-full font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 w-full justify-center border border-white/20 relative z-10"
                >
                  View Root Removal Pricing
                  <ArrowRight size={16} className="ml-2" />
                </a>
              </div>
            </div>

            {/* Milwaukee Air-Snake & P-Trap Service Card */}
            <div className="group bg-gradient-to-br from-neutral-gray to-authority-navy rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:scale-[1.02] md:col-span-2 lg:col-span-1 border border-authority-navy/20">
              <div className="p-8 text-white relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform relative z-10">
                  <Zap size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Air-Snake & P-Trap</h3>
                <p className="text-neutral-light mb-6 leading-relaxed relative z-10">
                  Quick solutions for common clogs using Milwaukee AirSnake and manual P-trap cleaning.
                </p>
                <div className="space-y-3 mb-6 relative z-10">
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">Air-Snake services: $250-$325</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">P-trap cleaning: $195-$225</span>
                  </div>
                  <div className="flex items-center text-white">
                    <CheckCircle size={16} className="mr-2 text-success-eco" />
                    <span className="text-sm font-medium">HVAC condensate: $250-$350</span>
                  </div>
                </div>
                <a 
                  href="/sewer-cleaning-pricing#air-snake-ptrap"
                  className="inline-flex items-center bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-full font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 w-full justify-center border border-white/20 relative z-10"
                >
                  View Quick Service Pricing
                  <ArrowRight size={16} className="ml-2" />
                </a>
              </div>
            </div>
          </div>

          {/* Final CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20">
              <h3 className="text-3xl font-bold mb-4">Not Sure Which Service You Need?</h3>
              <p className="text-xl mb-6 text-trust-light">
                Call us for a free consultation • Master plumber owned • 293+ verified reviews • Same-day service available
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="/sewer-repair-replacement" 
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                >
                  🔧 See Sewer Repair Services
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SewerCleaningPricing;